import * as React from "react";

import { Box, ListItemText, ListSubheader, Menu, MenuItem } from "@mui/material";
import { ButtonProps } from "@mui/material/Button";
import MenuList from "@mui/material/MenuList";
import { unstable_useId as useId } from "@mui/utils";
import { gridClasses } from "@mui/x-data-grid-pro";
import {
  bindMenu,
  bindTrigger,
  usePopupState,
} from "material-ui-popup-state/hooks";
import { CreateFab } from "../Buttons";
import {
  dropdownMenuItemSxFor,
  dropdownMenuListItemTextSx,
  dropdownMenuListProps,
  dropdownMenuNestedItemSx,
  dropdownMenuPaperProps,
  dropdownMenuSectionDividerSx,
  dropdownMenuSectionLabelSx,
} from "../dropdownMenuStyles";

export const CreateMenuContainer = React.forwardRef<
  HTMLButtonElement,
  ButtonProps
>(function CreateMenuContainer(props, ref) {
  const { children, onClick, ...other } = props;
  const exportButtonId = useId();
  const exportMenuId = useId();

  const popupState = usePopupState({
    variant: "popover",
    popupId: "export-menu",
  });

  if (children == null) {
    return null;
  }

  return (
    <React.Fragment>
      <CreateFab
        aria-label="Add / Create New"
        aria-haspopup="menu"
        id={exportButtonId}
        {...bindTrigger(popupState)}
        {...other}
        key={1} 
        size="small"
      />
      <Menu
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        {...bindMenu(popupState)}
      >
        <MenuList
          id={exportMenuId}
          className={gridClasses.menuList}
          aria-labelledby={exportButtonId}
          sx={{pt: 0, pb: 0}}
        >
          {React.Children.map(children, (child) => {
            if (!React.isValidElement(child)) {
              return child;
            }
            return React.cloneElement<any>(child, {
              hideMenu: popupState.close,
            });
          })}
        </MenuList>
      </Menu>
    </React.Fragment>
  );
});



export const CreateMenuOption = React.forwardRef<
  HTMLButtonElement,
  ButtonProps
>(function CreateMenu(props, ref) {
  const { children } = props;
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const fabRef = React.useRef(null);

  const toggleMenu = () => setIsMenuOpen((prev) => !prev);
  const closeMenu = () => setIsMenuOpen(false);

  return (
    <div>
      <CreateFab
      onClick={toggleMenu}
      size="small"
      />
      <CustomMenu
        isOpen={isMenuOpen}
        anchorRef={fabRef.current}
        onClose={closeMenu}
      >
        {children}
      </CustomMenu>
    </div>
  );
});


const CustomMenu   = ({ isOpen, anchorRef, onClose, children }) => {
  return (
    isOpen && (
      <ul
        style={{
          position: "absolute",
          top: anchorRef ? anchorRef.getBoundingClientRect().bottom : "70px",
          left: anchorRef ? anchorRef.getBoundingClientRect().left : "90px",
          backgroundColor: "#fff",
          boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
          borderRadius: "4px",
          listStyle: "none",
          padding: "8px 0",
          margin: 0,
          zIndex: 1000,
        }}
      >
        {React.Children.map(children, (child, index) => {
          return <>
          {child}
          </>
        })}
      </ul>
    )
  );
};

// ── Upstream (gitlab/develop 065f99602): sections-based create menu ─────────
// Added beside the older CreateMenuContainer / CreateMenuOption API, which
// local toolbars still import; upstream replaced the file wholesale.

export interface CreateMenuItem {
  name: string;
  onClick: () => void;
  hidden?: boolean;
  disabled?: boolean;
  /** LOCAL (H104): why a disabled item is inert, shown as its secondary line (Pattern 340 §2b). */
  disabledReason?: string;
}

export interface CreateMenuSection {
  label: string;
  items: CreateMenuItem[];
}

interface CreateMenuProps {
  sections: CreateMenuSection[];
  /** Return false to keep the menu closed (for example when the record is locked). */
  beforeOpen?: () => boolean;
}

/**
 * List-view + button. Opens a dropdown that uses the same panel, section
 * labels, and rows as the ACTIONS menu.
 */
export function CreateMenu({ sections, beforeOpen }: CreateMenuProps) {
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const close = () => setAnchorEl(null);

  const visibleSections = sections
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => !item.hidden),
    }))
    .filter((section) => section.items.length > 0);

  return (
    <>
      <CreateFab
        size="small"
        onClick={(event) => {
          if (beforeOpen && !beforeOpen()) return;
          setAnchorEl(event.currentTarget);
        }}
      />
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={close}
        PaperProps={dropdownMenuPaperProps}
        MenuListProps={dropdownMenuListProps}
      >
        {visibleSections.map((section, index) => (
          <React.Fragment key={`${section.label}-${index}`}>
            {index > 0 ? (
              <Box component="li" aria-hidden sx={dropdownMenuSectionDividerSx} />
            ) : null}
            <ListSubheader
              className="list-subheader-item"
              sx={dropdownMenuSectionLabelSx}
            >
              {section.label}
            </ListSubheader>
            {section.items.map((item, itemIndex) => {
              const reason = item.disabled ? item.disabledReason : undefined;
              return (
                <MenuItem
                  key={`${item.name}-${itemIndex}`}
                  sx={dropdownMenuItemSxFor(dropdownMenuNestedItemSx, reason)}
                  disabled={item.disabled}
                  onClick={() => {
                    close();
                    item.onClick();
                  }}
                >
                  <ListItemText sx={dropdownMenuListItemTextSx} primary={item.name} secondary={reason} />
                </MenuItem>
              );
            })}
          </React.Fragment>
        ))}
      </Menu>
    </>
  );
}
